import { Toaster } from "@/components/ui/toaster"
import CookiesBanner from "@/components/giveaway/CookiesBanner"

import { init as initAnalytics } from '@/lib/analytics';

// Initialize GA4 + Meta Pixel scripts
initAnalytics();

// Load Heebo font
const heeboLink = document.createElement('link');
heeboLink.href = 'https://fonts.googleapis.com/css2?family=Heebo:wght@300;400;500;600;700;800;900&display=swap';
heeboLink.rel = 'stylesheet';
document.head.appendChild(heeboLink);
const fontStyle = document.createElement('style');
fontStyle.textContent = 'body, * { font-family: "Heebo", sans-serif !important; }';
document.head.appendChild(fontStyle);
import { QueryClientProvider } from '@tanstack/react-query'
import { queryClientInstance } from '@/lib/query-client'
import { Route, Routes, Navigate } from 'react-router-dom';
import { useAuth, useUserProfile } from '@/lib/AuthContext';
import { Suspense, lazy, useEffect } from 'react';
import PageNotFound from './lib/PageNotFound';
import Giveaway from './pages/Giveaway';
import GiveawayCopy from './pages/GiveawayCopy';
import CampaignProfileTasks from './pages/CampaignProfileTasks';
import Festival from './pages/Festival';
import Dashboard from './pages/Dashboard';
import Leaderboard from './pages/Leaderboard';
import Profile from './pages/Profile';
import SignIn from './pages/SignIn';
import LandingV3 from './pages/LandingV3';
import Survey from './pages/Survey';
import PrivacyPolicy from './pages/PrivacyPolicy';
import DataDeletion from './pages/DataDeletion';
import TermsOfService from './pages/TermsOfService';

// Lazy-load admin pages — regular users never download this code
const AdminQueue = lazy(() => import('./pages/AdminQueue'));
const AdminDashboard = lazy(() => import('./pages/AdminDashboard'));

const LoadingSpinner = () => (
  <div className="min-h-screen flex items-center justify-center" style={{ background: "#0a0a0a" }}>
    <div className="w-10 h-10 border-4 border-transparent rounded-full animate-spin" style={{ borderTopColor: "#c8f135" }} />
  </div>
);

function ProtectedRoute({ children }) {
  const { isAuthenticated, isLoadingAuth, login } = useAuth();

  useEffect(() => {
    if (!isLoadingAuth && !isAuthenticated) {
      login();
    }
  }, [isLoadingAuth, isAuthenticated]);

  if (isLoadingAuth || !isAuthenticated) {
    return <LoadingSpinner />;
  }

  return children;
}

function AdminRoute({ children }) {
  const { isAdmin, isLoading, isAuthenticated } = useUserProfile();
  const { isLoadingAuth, login } = useAuth();

  useEffect(() => {
    if (!isLoadingAuth && !isAuthenticated) {
      login();
    }
  }, [isLoadingAuth, isAuthenticated]);

  if (isLoadingAuth || !isAuthenticated || isLoading) {
    return <LoadingSpinner />;
  }

  if (!isAdmin) {
    return <Navigate to="/" replace />;
  }

  return children;
}

const AuthenticatedApp = () => {
  return (
    <Suspense fallback={<LoadingSpinner />}>
      <Routes>
        {/* Public routes */}
        <Route path="/" element={<GiveawayCopy />} />
        <Route path="/giveaway" element={<Giveaway />} />
        <Route path="/festival" element={<Festival />} />
        <Route path="/leaderboard" element={<Leaderboard />} />
        <Route path="/signin" element={<SignIn />} />
        <Route path="/landing" element={<LandingV3 />} />
        <Route path="/privacy" element={<PrivacyPolicy />} />
        <Route path="/terms" element={<TermsOfService />} />
        <Route path="/data-deletion" element={<DataDeletion />} />

        {/* Authenticated routes */}
        <Route path="/tasks" element={<ProtectedRoute><CampaignProfileTasks /></ProtectedRoute>} />
        <Route path="/dashboard" element={<ProtectedRoute><Dashboard /></ProtectedRoute>} />
        <Route path="/profile" element={<ProtectedRoute><Profile /></ProtectedRoute>} />
        <Route path="/survey" element={<ProtectedRoute><Survey /></ProtectedRoute>} />

        {/* Admin routes (lazy-loaded, role-gated) */}
        <Route path="/admin" element={<AdminRoute><AdminDashboard /></AdminRoute>} />
        <Route path="/admin/queue" element={<AdminRoute><AdminQueue /></AdminRoute>} />

        {/* Legacy uppercase redirects (backwards compatibility) */}
        <Route path="/Giveaway" element={<Navigate to="/giveaway" replace />} />
        <Route path="/GiveawayCopy" element={<Navigate to="/" replace />} />
        <Route path="/CampaignProfileTasks" element={<Navigate to="/tasks" replace />} />
        <Route path="/Festival" element={<Navigate to="/festival" replace />} />
        <Route path="/Dashboard" element={<Navigate to="/dashboard" replace />} />
        <Route path="/EngagementDashboard" element={<Navigate to="/dashboard" replace />} />
        <Route path="/Leaderboard" element={<Navigate to="/leaderboard" replace />} />
        <Route path="/Profile" element={<Navigate to="/profile" replace />} />
        <Route path="/AdminQueue" element={<Navigate to="/admin/queue" replace />} />
        <Route path="/AdminDashboard" element={<Navigate to="/admin" replace />} />
        <Route path="/SignIn" element={<Navigate to="/signin" replace />} />

        <Route path="*" element={<PageNotFound />} />
      </Routes>
    </Suspense>
  );
};


function App() {
  return (
    <QueryClientProvider client={queryClientInstance}>
      <AuthenticatedApp />
      <Toaster />
      <CookiesBanner />
    </QueryClientProvider>
  )
}

export default App
